import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import type {
	BaseEventPayload,
	DropTargetLocalizedData,
	ElementDragType,
} from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import {
	draggable,
	dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box";
import clsx from "clsx";
import { type HTMLAttributes, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import type { ChildrenProps } from "../types";
import type { DraggableItem } from "./types";

/** Props Interface */
interface DraggableProps<T>
	extends ChildrenProps,
		Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	className?: string;
	id?: string;
	style?: React.CSSProperties;
	type: DraggableItem;
	onClick?: () => void;
	onDoubleClick?: () => void;
	isDisabled?: boolean;
	onDragStart?: () => void;
	handleOnDrop?: (payload: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
	item: T;
	allowedEdges?: Array<Edge>;
	defaultCursor?: boolean;
	accept?: Array<DraggableItem>;
	canDrag?: boolean;
	onGenerateDragPreview?: (args: {
		source: BaseEventPayload<ElementDragType>;
		element: HTMLElement;
	}) => HTMLElement | null;
	Preview?: () => React.ReactNode;
}

type State =
	| {
			type: "idle";
	  }
	| {
			type: "preview";
			container: HTMLElement;
	  };

export function Draggable<T>({
	accept = [],
	canDrag = true,
	children,
	id,
	className,
	style,
	type,
	onClick,
	onDoubleClick,
	isDisabled,
	onDragStart,
	item,
	allowedEdges,
	handleOnDrop,
	defaultCursor,
	Preview,
	...rest
}: DraggableProps<T>) {
	/** State */
	const [isDragging, setIsDragging] = useState(false);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [state, setState] = useState<State>({ type: "idle" });

	/** Hooks */
	const ref = useRef<HTMLDivElement>(null);

	/** Effects */
	useEffect(() => {
		if (isDisabled) return;
		if (!ref.current) return;
		const el = ref.current;
		const cleanup = combine(
			dropTargetForElements({
				element: el,
				canDrop: (data) => {
					if (accept.includes(data.source.data.type as DraggableItem)) return true;
					return false;
				},
				getData: ({ input, element }) => {
					return attachClosestEdge(
						{},
						{
							input,
							element,
							allowedEdges: allowedEdges ?? [],
						},
					);
				},
				onDragEnter: ({ self }) => {
					const closestEdge = extractClosestEdge(self.data);
					if (closestEdge) {
						setClosestEdge(closestEdge);
					}
				},
				onDrag: ({ self }) => {
					const closestEdge = extractClosestEdge(self.data);
					if (closestEdge) {
						setClosestEdge(closestEdge);
					} else {
						setClosestEdge(null);
					}
				},
				onDragLeave: () => setClosestEdge(null),
				onDrop: (payload) => {
					handleOnDrop?.(payload);
					setClosestEdge(null);
				},
			}),
			draggable({
				canDrag: () => canDrag,
				element: el,
				getInitialData: () => ({ type, item }),
				onDragStart: () => {
					onDragStart?.();
					setIsDragging(true);
				},
				onGenerateDragPreview({ nativeSetDragImage }) {
					if (Preview) {
						setCustomNativeDragPreview({
							render({ container }) {
								setState({ type: "preview", container });
								return () => setState({ type: "idle" });
							},
							nativeSetDragImage,
						});
					}
				},
				onDrop: () => {
					setIsDragging(false);
				},
			}),
		);
		function handleNativeDragStart(event: DragEvent) {
			if (event.dataTransfer) {
				event.dataTransfer.effectAllowed = "move";
				event.dataTransfer.setData("application/json", JSON.stringify(item));
			}
		}

		const elCurrent = ref.current;
		elCurrent.addEventListener("dragstart", handleNativeDragStart);

		return () => {
			cleanup();
			el.removeEventListener("dragstart", handleNativeDragStart);
		};
	}, [type, isDisabled, item, onDragStart, allowedEdges]);

	/** Render */
	return (
		<div
			id={id}
			ref={ref}
			className={clsx(
				"relative",
				!isDisabled && !defaultCursor && "cursor-grab active:cursor-grabbing",
				isDragging && "opacity-0",
				className,
			)}
			style={{
				opacity: isDragging ? 0 : 1,
				...style,
			}}
			onClick={onClick}
			onKeyUp={() => {}}
			onDoubleClick={onDoubleClick}
			{...rest}
		>
			{children}
			{closestEdge && (
				<DropIndicator
					key="drop-indicator"
					type="terminal-no-bleed"
					edge={closestEdge}
				/>
			)}

			{Preview
				? state.type === "preview"
					? ReactDOM.createPortal(<Preview />, state.container)
					: null
				: null}
		</div>
	);
}
